import { identity } from 'lodash-es';
import qs from 'query-string';
import { getEnvironmentConfigValue } from 'config';
import { http } from 'config';
import { getPathVariableNames } from 'services/utils';
import {
  ApiServiceEndpointHandler,
  CreateEndpointTemplate,
  ApiServiceRequestConfigOverride,
  EnvironmentConfig,
  ResponseHeaders,
} from 'types';
import { PathVariables } from '../types';
import {
  MutationEndpointFactoryConfig,
  ServiceEndpointFactoryConfig,
  ServiceFactoryOptions,
} from './types';
import {
  getDataFromArgs,
  getPathVariablesFromArgs,
  getQueryParamsFromArgs,
  replacePathVariables,
} from './utils';

/**
 * A class used to create functions that integrate with API REST endpoints
 *
 * The generics used in this classare as follows:
 *  - TPath: the endpoint path with path variables like `/asset/:id`
 *  - TArgs: the arguments that the endpoint handler accepts
 *  - TData: the request payload for endpoints that support it
 *  - TQueryResult: the shape of the data returned by the API endpoint
 *  - TResult: the shape of the data returned by the endpoint handler
 *
 * A note on the curried functions -
 *
 * The "create" endpoints take the form of `create(path)(options)`. The only reason
 * it's written this way is to make the types easier to work with.
 *
 * Typescript can infer all of the generics or none of them.  So, if all of the
 * generics were on the same function, including `TPath`, then in order to specify
 * argument and return value types, the type of `TPath` would have to be provided
 * as well, or it would default to `string` and type-safety for path variable
 * replacement would be lost.  Specifying the type of `TPath` is messy:
 *
 *  create<typeof PATH, Args, Result>(path, opts);
 *
 * By accepting `path` in the first function it can always be inferred:
 *
 *  create(path)<Args, Result>(opts)
 */
export default class ServiceFactory {
  private baseUrlValue: string | undefined = undefined;

  constructor(
    private baseUrlConfig: EnvironmentConfig,
    private config?: ServiceFactoryOptions,
  ) {}

  // NB: this function should be called in the handler function because it has to
  // be called after the library is configured and so cannot be called in the course
  // of creating the endpoint handler
  private createEndpointTemplateFactory<TPath extends string>(path: TPath) {
    return (() => this.baseUrl + path) as CreateEndpointTemplate;
  }

  private createEndpointUrlFactory<
    TPath extends string,
    TArgs,
    TQueryResult = unknown,
    TResult = TQueryResult,
  >(
    path: TPath,
    pathVarNames: Array<keyof PathVariables<TPath>>,
    config?: ServiceEndpointFactoryConfig<TPath, TArgs, TQueryResult, TResult>,
  ) {
    return ((args?: TArgs) => {
      const pathVars = getPathVariablesFromArgs(
        pathVarNames,
        config,
        args as TArgs,
      );
      const queryParams = getQueryParamsFromArgs(config, args as TArgs);

      return qs.stringifyUrl({
        url: this.buildRequestPath(path, pathVars),
        query: queryParams,
      });
    }) as ApiServiceEndpointHandler<TArgs, TResult>['url'];
  }

  public get baseUrl() {
    if (!this.baseUrlValue) {
      this.baseUrlValue = getEnvironmentConfigValue(this.baseUrlConfig);
    }

    return this.baseUrlValue;
  }

  private buildRequestPath<TPath extends string>(
    path: TPath,
    pathVariables?: PathVariables<TPath>,
  ): string {
    const resolvedPath = !pathVariables
      ? path
      : replacePathVariables(path, pathVariables);
    return this.baseUrl + resolvedPath;
  }

  public createGetEndpoint<TPath extends string>(path: TPath) {
    const pathVarNames = getPathVariableNames(path);

    return <TArgs = unknown, TQueryResult = unknown, TResult = TQueryResult>(
      config?: ServiceEndpointFactoryConfig<
        TPath,
        TArgs,
        TQueryResult,
        TResult
      >,
    ) => {
      const {
        disableAuthHeader,
        omitBearerPrefix = this.config?.omitBearerPrefix,
        responseType,
        select = identity,
        withCredentials,
      } = config ?? {};

      const endpoint = (async (
        args: TArgs,
        opts?: ApiServiceRequestConfigOverride,
      ) => {
        const pathVars = getPathVariablesFromArgs(pathVarNames, config, args);
        const params = getQueryParamsFromArgs(config, args);
        const url = this.buildRequestPath(path, pathVars);

        const result = await http().get<TQueryResult>(url, {
          disableAuthHeader,
          omitBearerPrefix,
          params,
          responseType,
          withCredentials,
          ...opts,
        });

        return select(result);
      }) as ApiServiceEndpointHandler<TArgs, TResult>;

      endpoint.template = this.createEndpointTemplateFactory(path);
      endpoint.url = this.createEndpointUrlFactory(path, pathVarNames, config);

      return endpoint;
    };
  }

  public createDeleteEndpoint<TPath extends string>(path: TPath) {
    const pathVarNames = getPathVariableNames(path);

    return <TArgs = unknown, TQueryResult = unknown, TResult = TQueryResult>(
      config?: ServiceEndpointFactoryConfig<
        TPath,
        TArgs,
        TQueryResult,
        TResult
      >,
    ) => {
      const {
        disableAuthHeader,
        omitBearerPrefix = this.config?.omitBearerPrefix,
        select = identity,
        withCredentials,
      } = config ?? {};

      const endpoint = (async (
        args: TArgs,
        opts?: ApiServiceRequestConfigOverride,
      ) => {
        const pathVars = getPathVariablesFromArgs(pathVarNames, config, args);
        const params = getQueryParamsFromArgs(config, args);
        const url = this.buildRequestPath(path, pathVars);

        const result = await http().delete<TQueryResult>(url, {
          disableAuthHeader,
          omitBearerPrefix,
          params,
          withCredentials,
          ...opts,
        });

        return select(result);
      }) as ApiServiceEndpointHandler<TArgs, TResult>;

      endpoint.template = this.createEndpointTemplateFactory(path);
      endpoint.url = this.createEndpointUrlFactory(path, pathVarNames, config);

      return endpoint;
    };
  }

  public createHeadEndpoint<TPath extends string>(path: TPath) {
    const pathVarNames = getPathVariableNames(path);

    return <TArgs = unknown, TResult = ResponseHeaders>(
      config?: ServiceEndpointFactoryConfig<
        TPath,
        TArgs,
        ResponseHeaders,
        TResult
      >,
    ) => {
      const {
        disableAuthHeader,
        omitBearerPrefix = this.config?.omitBearerPrefix,
        select = identity,
        withCredentials,
      } = config ?? {};

      const endpoint = (async (
        args: TArgs,
        opts?: ApiServiceRequestConfigOverride,
      ) => {
        const pathVars = getPathVariablesFromArgs(pathVarNames, config, args);
        const params = getQueryParamsFromArgs(config, args);
        const url = this.buildRequestPath(path, pathVars);

        const result = await http().head(url, {
          disableAuthHeader,
          omitBearerPrefix,
          params,
          withCredentials,
          ...opts,
        });

        return select(result);
      }) as ApiServiceEndpointHandler<TArgs, TResult>;

      endpoint.template = this.createEndpointTemplateFactory(path);
      endpoint.url = this.createEndpointUrlFactory(path, pathVarNames, config);

      return endpoint;
    };
  }

  public createPostEndpoint<TPath extends string>(path: TPath) {
    const pathVarNames = getPathVariableNames(path);

    return <
      TArgs = unknown,
      TQueryResult = unknown,
      TResult = TQueryResult,
      TData extends object | undefined = undefined,
    >(
      config?: MutationEndpointFactoryConfig<
        TPath,
        TArgs,
        TData,
        TQueryResult,
        TResult
      >,
    ) => {
      const {
        disableAuthHeader,
        omitBearerPrefix = this.config?.omitBearerPrefix,
        select = identity,
        withCredentials,
      } = config ?? {};

      const endpoint = (async (
        args: TArgs,
        opts?: ApiServiceRequestConfigOverride,
      ) => {
        const data = getDataFromArgs(config, args);
        const pathVars = getPathVariablesFromArgs(pathVarNames, config, args);
        const params = getQueryParamsFromArgs(config, args);
        const url = this.buildRequestPath(path, pathVars);

        const result = await http().post<TQueryResult, TData>(url, {
          data,
          disableAuthHeader,
          omitBearerPrefix,
          params,
          withCredentials,
          ...opts,
        });

        return select(result);
      }) as ApiServiceEndpointHandler<TArgs, TResult>;

      endpoint.template = this.createEndpointTemplateFactory(path);
      endpoint.url = this.createEndpointUrlFactory(path, pathVarNames, config);

      return endpoint;
    };
  }

  public createPutEndpoint<TPath extends string>(path: TPath) {
    const pathVarNames = getPathVariableNames(path);

    return <
      TArgs = unknown,
      TQueryResult = unknown,
      TResult = TQueryResult,
      TData extends object | undefined = undefined,
    >(
      config?: MutationEndpointFactoryConfig<
        TPath,
        TArgs,
        TData,
        TQueryResult,
        TResult
      >,
    ) => {
      const {
        disableAuthHeader,
        omitBearerPrefix = this.config?.omitBearerPrefix,
        select = identity,
        withCredentials,
      } = config ?? {};

      const endpoint = (async (
        args: TArgs,
        opts?: ApiServiceRequestConfigOverride,
      ) => {
        const data = getDataFromArgs(config, args);
        const pathVars = getPathVariablesFromArgs(pathVarNames, config, args);
        const params = getQueryParamsFromArgs(config, args);
        const url = this.buildRequestPath(path, pathVars);

        const result = await http().put<TQueryResult, TData>(url, {
          data,
          disableAuthHeader,
          omitBearerPrefix,
          params,
          withCredentials,
          ...opts,
        });

        return select(result);
      }) as ApiServiceEndpointHandler<TArgs, TResult>;

      endpoint.template = this.createEndpointTemplateFactory(path);
      endpoint.url = this.createEndpointUrlFactory(path, pathVarNames, config);

      return endpoint;
    };
  }
}
